import { Head, router } from "@inertiajs/react"
import { ArrowLeft, Pencil, Plus, Printer, Trash2 } from "lucide-react"
import { useEffect, useState } from "react"

import { Button } from "@/components/ui/button"
import { DataTable, Td, Th, Thead, Tr } from "@/components/steel/table"
import { Input } from "@/components/ui/input"
import { PageHeader } from "@/components/steel"
import { Textarea } from "@/components/ui/textarea"
import { formatCurrency } from "@/lib/utils"
import type { Bid, BidLineItem, BidMilestone } from "@/lib/bids"

function fmtCur(n: number): string {
  return formatCurrency(Math.round(n))
}

function numberToWords(n: number): string {
  if (n === 0) return "Zero dollars"
  const ones = ["", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine",
    "ten", "eleven", "twelve", "thirteen", "fourteen", "fifteen", "sixteen", "seventeen", "eighteen", "nineteen"]
  const tens = ["", "", "twenty", "thirty", "forty", "fifty", "sixty", "seventy", "eighty", "ninety"]

  function chunk(num: number): string {
    if (num === 0) return ""
    if (num < 20) return ones[num]
    if (num < 100) return `${tens[Math.floor(num / 10)]}${num % 10 ? `-${ones[num % 10]}` : ""}`
    if (num < 1000) return `${ones[Math.floor(num / 100)]} hundred${num % 100 ? ` ${chunk(num % 100)}` : ""}`
    return ""
  }

  const parts: string[] = []
  const units = ["", "thousand", "million", "billion"]
  let remaining = Math.abs(Math.round(n))
  let unitIndex = 0

  while (remaining > 0) {
    const c = remaining % 1000
    if (c > 0) {
      const word = chunk(c)
      parts.unshift(units[unitIndex] ? `${word} ${units[unitIndex]}` : word)
    }
    remaining = Math.floor(remaining / 1000)
    unitIndex++
  }

  const result = parts.join(", ")
  return `${result.charAt(0).toUpperCase()}${result.slice(1)} dollars`
}

function formatLetterDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })
}

function Editable({
  children,
  onEdit,
}: {
  children: React.ReactNode
  onEdit: () => void
}) {
  return (
    <div
      className="group relative -m-2 cursor-pointer rounded-md border border-transparent p-2 transition-colors hover:border-border print:m-0 print:border-transparent print:p-0"
      onClick={onEdit}
      onKeyDown={(e) => e.key === "Enter" && onEdit()}
      role="button"
      tabIndex={0}
    >
      {children}
      <Pencil className="absolute right-2 top-2 h-3 w-3 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 print:hidden" />
    </div>
  )
}

export default function BidProposal({ bid }: { bid: Bid }) {
  const [editing, setEditing] = useState<"opening" | "closing" | "items" | "milestones" | null>(null)
  const [opening, setOpening] = useState(bid.proposalMessage ?? "")
  const [closing, setClosing] = useState(bid.closingMessage ?? "")
  const [items, setItems] = useState<BidLineItem[]>(bid.lineItems ?? [])
  const [milestones, setMilestones] = useState<BidMilestone[]>(bid.milestones ?? [])

  useEffect(() => setMilestones(bid.milestones ?? []), [bid.milestones])

  const { calculated: calc, contact, sender } = bid
  const dateStr = formatLetterDate(bid.createdAt)

  const lineItems = bid.lineItems.length > 0
    ? bid.lineItems
    : [{ id: "auto-1", description: "Total for all labor and material", amount: calc.price ?? calc.grandTotal }]
  const total = lineItems.reduce((sum, li) => sum + li.amount, 0)
  const durationWeeks = Math.max(1, Math.ceil(calc.totalWeeks))
  const billingTotal = milestones.reduce((sum, m) => sum + m.billingPct, 0)

  const save = (data: Partial<Bid>) => {
    router.patch(`/bids/${bid.id}`, data as never, { preserveScroll: true })
    setEditing(null)
  }

  return (
    <>
      <Head title={`Proposal ${bid.id}`} />

      <div className="print:hidden">
        <PageHeader
          leading={
            <Button variant="ghost" size="icon-sm" onClick={() => router.visit(`/bids/${bid.id}/edit`)}>
              <ArrowLeft />
            </Button>
          }
          title="Proposal"
          meta="Click any block to edit in place"
          actions={
            <Button variant="outline" size="sm" onClick={() => window.print()}>
              <Printer />
              Print
            </Button>
          }
        />
      </div>

      <div className="flex justify-center px-6 pb-10 pt-6 print:p-0">
        <article className="proposal-sheet w-full max-w-[720px] rounded-lg border border-border bg-card px-12 py-11 print:max-w-none print:rounded-none print:border-0 print:p-0">
          <header className="flex items-start justify-between gap-5 border-b border-border pb-5">
            <div>
              <p className="text-title font-semibold">{sender.companyName || "Real Steel Solutions"}</p>
              {sender.address ? <p className="mt-0.5 text-label text-muted-foreground">{sender.address}</p> : null}
              <p className="text-label text-muted-foreground">
                {[sender.phone, sender.email].filter(Boolean).join(" · ")}
              </p>
            </div>
            <p className="num text-right text-label text-muted-foreground">
              {dateStr}
              <br />
              {bid.id} · Rev {bid.revision}
            </p>
          </header>

          <div className="pt-5">
            {contact.clientName ? <p className="text-body font-medium">{contact.clientName}</p> : null}
            {contact.companyName ? <p className="text-body text-muted-foreground">{contact.companyName}</p> : null}
            {contact.address ? <p className="text-body text-muted-foreground">{contact.address}</p> : null}

            <p className="mt-5 text-body">
              <span className="font-semibold">RE:</span> {bid.jobName || "Steel structure project"}
              {bid.jobLocation ? ` — ${bid.jobLocation}` : ""}
            </p>

            <p className="mt-4 text-body leading-loose">
              Dear {contact.clientName ? contact.clientName.split(" ")[0] : "Sir or Madam"},
            </p>

            <div className="mt-2.5">
              {editing === "opening" ? (
                <div className="flex flex-col gap-2">
                  <Textarea rows={5} value={opening} onChange={(e) => setOpening(e.target.value)} />
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={() => setEditing(null)}>
                      Cancel
                    </Button>
                    <Button size="sm" onClick={() => save({ proposalMessage: opening })}>
                      Save
                    </Button>
                  </div>
                </div>
              ) : (
                <Editable onEdit={() => { setOpening(bid.proposalMessage ?? ""); setEditing("opening") }}>
                  {bid.proposalMessage ? (
                    <p className="whitespace-pre-wrap text-body leading-loose">{bid.proposalMessage}</p>
                  ) : (
                    <p className="text-body italic text-muted-foreground">Click to add the opening paragraph…</p>
                  )}
                </Editable>
              )}
            </div>

            <p className="mb-2.5 mt-6 text-body font-semibold">Scope</p>
            {editing === "items" ? (
              <div className="flex flex-col gap-2">
                {items.map((li) => (
                  <div key={li.id} className="flex items-center gap-2">
                    <Input
                      value={li.description}
                      onChange={(e) => setItems((prev) => prev.map((r) => (r.id === li.id ? { ...r, description: e.target.value } : r)))}
                      placeholder="Description"
                      className="flex-1"
                    />
                    <Input
                      type="number"
                      value={li.amount || ""}
                      onChange={(e) => setItems((prev) => prev.map((r) => (r.id === li.id ? { ...r, amount: Number.parseFloat(e.target.value) || 0 } : r)))}
                      className="num w-36 text-right"
                    />
                    <Button variant="ghost" size="icon-sm" onClick={() => setItems((prev) => prev.filter((r) => r.id !== li.id))}>
                      <Trash2 />
                    </Button>
                  </div>
                ))}
                <div className="flex items-center justify-between">
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={() => setItems((prev) => [...prev, { id: `li-${Date.now()}`, description: "", amount: 0 }])}
                  >
                    <Plus />
                    Add line
                  </Button>
                  <span className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setEditing(null)}>
                      Cancel
                    </Button>
                    <Button size="sm" onClick={() => save({ lineItems: items })}>
                      Save
                    </Button>
                  </span>
                </div>
              </div>
            ) : (
              <Editable onEdit={() => { setItems([...lineItems]); setEditing("items") }}>
                {lineItems.map((li) => (
                  <div key={li.id} className="flex items-baseline gap-2.5 border-t border-border py-2">
                    <span className="flex-1 text-body">{li.description}</span>
                    <span className="num text-label text-muted-foreground">{fmtCur(li.amount)}</span>
                  </div>
                ))}
              </Editable>
            )}

            <div className="mt-4 flex items-baseline justify-between border-t border-foreground pt-3.5">
              <span className="text-body font-semibold">Total for all labor and material</span>
              <span className="num text-[20px] font-semibold tracking-[-0.02em] text-whisper">{fmtCur(total)}</span>
            </div>
            <p className="mt-1.5 text-right text-label text-muted-foreground">{numberToWords(total)}</p>

            {milestones.length > 0 ? (
              <>
                <p className="mb-2.5 mt-6 text-body font-semibold">Estimated progress billing</p>

                {editing === "milestones" ? (
                  <div className="flex flex-col gap-2">
                    {milestones.map((ms) => (
                      <div key={ms.id} className="flex items-center gap-2">
                        <Input
                          value={ms.label}
                          onChange={(e) => setMilestones((prev) => prev.map((m) => (m.id === ms.id ? { ...m, label: e.target.value } : m)))}
                          placeholder="Milestone"
                          className="flex-1"
                        />
                        <Input
                          type="number"
                          value={ms.completionPct || ""}
                          onChange={(e) => setMilestones((prev) => prev.map((m) => (m.id === ms.id ? { ...m, completionPct: Number.parseFloat(e.target.value) || 0 } : m)))}
                          className="num w-20 text-right"
                          title="% complete"
                        />
                        <Input
                          type="number"
                          value={ms.billingPct || ""}
                          onChange={(e) => setMilestones((prev) => prev.map((m) => (m.id === ms.id ? { ...m, billingPct: Number.parseFloat(e.target.value) || 0 } : m)))}
                          className="num w-20 text-right"
                          title="% billed"
                        />
                        <Button variant="ghost" size="icon-sm" onClick={() => setMilestones((prev) => prev.filter((m) => m.id !== ms.id))}>
                          <Trash2 />
                        </Button>
                      </div>
                    ))}
                    <div className="flex items-center justify-between">
                      <Button
                        variant="ghost"
                        size="xs"
                        onClick={() => setMilestones((prev) => [...prev, { id: `ms-${Date.now()}`, label: "", completionPct: 0, billingPct: 0 }])}
                      >
                        <Plus />
                        Add milestone
                      </Button>
                      <span className="flex items-center gap-2">
                        {billingTotal !== 100 ? (
                          <span className="text-meta text-destructive">Billing totals {billingTotal}% — must be 100%</span>
                        ) : null}
                        <Button variant="outline" size="sm" onClick={() => setEditing(null)}>
                          Cancel
                        </Button>
                        <Button size="sm" disabled={billingTotal !== 100} onClick={() => save({ milestones })}>
                          Save
                        </Button>
                      </span>
                    </div>
                  </div>
                ) : (
                  <Editable onEdit={() => setEditing("milestones")}>
                    <div className="mb-3 flex h-1.5 overflow-hidden rounded-full">
                      {milestones.map((ms, index) => (
                        <span
                          key={ms.id}
                          className="h-1.5 border-r border-card bg-foreground"
                          style={{ width: `${ms.billingPct}%`, opacity: 1 - index * 0.22 }}
                        />
                      ))}
                    </div>
                    <DataTable minWidth={520}>
                      <Thead>
                        <Th>Milestone</Th>
                        <Th align="right">At completion</Th>
                        <Th align="right">Billing</Th>
                        <Th align="right">Amount due</Th>
                        <Th align="right">Cumulative</Th>
                      </Thead>
                      <tbody>
                        {(() => {
                          let cumulative = 0
                          return milestones.map((ms) => {
                            const amount = total * (ms.billingPct / 100)
                            cumulative += amount
                            return (
                              <Tr key={ms.id} className="hover:bg-transparent">
                                <Td strong>{ms.label}</Td>
                                <Td align="right" num muted>{ms.completionPct}%</Td>
                                <Td align="right" num>{ms.billingPct}%</Td>
                                <Td align="right" num accent>{fmtCur(amount)}</Td>
                                <Td align="right" num muted>{fmtCur(cumulative)}</Td>
                              </Tr>
                            )
                          })
                        })()}
                      </tbody>
                    </DataTable>
                  </Editable>
                )}
              </>
            ) : null}

            <div className="mt-6 flex items-center justify-between rounded-lg border border-border bg-background px-3.5 py-3">
              <span className="text-body font-medium">Estimated project timeline</span>
              <span className="num text-body text-muted-foreground">
                ~{durationWeeks} weeks · {calc.totalManHrs.toLocaleString(undefined, { maximumFractionDigits: 0 })} man hours
              </span>
            </div>

            <div className="mt-6">
              {editing === "closing" ? (
                <div className="flex flex-col gap-2">
                  <Textarea rows={3} value={closing} onChange={(e) => setClosing(e.target.value)} />
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={() => setEditing(null)}>
                      Cancel
                    </Button>
                    <Button size="sm" onClick={() => save({ closingMessage: closing })}>
                      Save
                    </Button>
                  </div>
                </div>
              ) : (
                <Editable onEdit={() => { setClosing(bid.closingMessage ?? ""); setEditing("closing") }}>
                  {bid.closingMessage ? (
                    <p className="whitespace-pre-wrap text-body leading-loose">{bid.closingMessage}</p>
                  ) : (
                    <p className="text-body italic text-muted-foreground">Click to add a closing paragraph…</p>
                  )}
                </Editable>
              )}
            </div>

            <p className="mt-6 text-body">Sincerely,</p>
            <p className="mt-4 text-body font-semibold">{sender.name || "—"}</p>
            <p className="text-label text-muted-foreground">{sender.companyName || "—"}</p>

            <p className="mt-7 border-t border-border pt-3.5 text-meta text-muted-foreground">
              This proposal is valid for 30 days from the date of issue.
            </p>
          </div>
        </article>
      </div>
    </>
  )
}
